/**
 * Wrappers for sending HTTP requests to the staffMemberships API.
 *
 * Provided by the StaffMemberships store, but does not directly interact with the store.
 */
import URI from 'jsuri';
import Q from 'q';

import type { API as Api } from 'js/lib/api';
import API from 'js/lib/api';

import type {
  ObjectId,
  StaffMembershipInterface,
  StaffMembershipRoleInterface,
  StaffMembershipType,
  StaffMembershipTypeName,
  StaffMembershipUserInterface,
} from '../types';

const _api = API('/api/staffMemberships.v1/', { type: 'rest' });

export type StaffMembershipsAPICreateInterface = {
  /** User email is the unique user identifier during create. */
  email: string;
  roleId: string;
  scope: {
    typeName: StaffMembershipTypeName;
    id: string | number;
  };
};

/**
 * Responses to a create request will be an associative array keyed by emails.
 * A non-empty array means the membership was created.
 * An empty array means the email does not belong to a coursera user.
 */
export type StaffMembershipsAPICreateResponseInterface = {
  result: {
    [email: string]: Array<string>;
  };
};

/**
 * Create new memberships for users.
 */
export const create = ({
  /** The membership objects we need to create. */
  memberships,
  // DI API, mainly for testing.
  api = _api,
}: {
  memberships: Array<StaffMembershipsAPICreateInterface>;
  api?: Api;
}): Q.Promise<StaffMembershipsAPICreateResponseInterface> => {
  const uri = new URI().addQueryParam('action', 'addStaff');
  return Q(
    api.post<StaffMembershipsAPICreateResponseInterface>(uri.toString(), {
      data: memberships,
    })
  );
};

/**
 * Unlink 1 to N numbers of memberships.
 */
export const del = ({
  /**
   * Ids we wish to delete.
   *
   * The membership ids are themselves compound keys and contain the entity information
   * needed to remove them from the backend.
   */
  ids,

  // DI API, mainly for testing.
  api = _api,
}: {
  ids: ObjectId | Array<ObjectId>;
  api?: Api;
}): Q.Promise<void> => {
  const uri = new URI().addQueryParam('action', 'deleteStaffMemberships');
  return Q(
    api.post(uri.toString(), {
      data: {
        ids: Array.isArray(ids) ? ids : [ids],
      },
    })
  );
};

/** Objects returned from a request. */
export type StaffMembershipsAPIGetResponseInterface = {
  elements: Array<StaffMembershipInterface>;
  /** Unused, but likely returned in the response envelope. */
  paging?: {};
  linked: {
    'partnerRoleTemplates.v1': Array<StaffMembershipRoleInterface>;
    'users.v1': Array<StaffMembershipUserInterface>;
  };
};

/**
 * Get memberships by id.
 */
export const get = ({
  /** Membership ids to retrieve. */
  ids,

  // DI API, mainly for testing.
  api = _api,
}: {
  ids: ObjectId | Array<ObjectId>;
  api?: Api;
}): Q.Promise<StaffMembershipsAPIGetResponseInterface> => {
  const uri = new URI()
    .addQueryParam('fields', 'users.v1(fullName,email),partnerRoleTemplates.v1(displayName)')
    .addQueryParam('includes', 'users,role')
    .addQueryParam('ids', (!Array.isArray(ids) ? [ids] : ids).join(','));

  return Q(api.get<StaffMembershipsAPIGetResponseInterface>(uri.toString()));
};

/**
 * Get all the memberships related to a particular entity of a particular type.
 */
export const getAll = ({
  /** The id of the `type` from which to retrieve memberships. */
  fromId,
  /** Optional, and only used when type='course'. */
  sessionId,
  /** Generic names for the various ways course, partner, and program types are referenced. */
  type,

  // DI API, mainly for testing.
  api = _api,
}: {
  fromId: ObjectId;
  sessionId?: ObjectId;
  type: keyof typeof StaffMembershipType;
  api?: Api;
}): Q.Promise<StaffMembershipsAPIGetResponseInterface> => {
  const typeToQuery = {
    course: 'allByCourse',
    partner: 'allByPartner',
    program: 'allByProgram',
  };
  const q = typeToQuery[type];
  const typeToIdKey = {
    course: 'courseId',
    partner: 'partnerId',
    program: 'degreeId',
  };
  const idKey = typeToIdKey[type];
  const uri = new URI()
    .addQueryParam('fields', 'users.v1(fullName,email),partnerRoleTemplates.v1(displayName)')
    .addQueryParam('includes', 'users,role')
    .addQueryParam('q', q)
    .addQueryParam(idKey, fromId);
  if (type === 'course' && sessionId) {
    uri.addQueryParam('sessionId', sessionId);
  }

  return Q(api.get<StaffMembershipsAPIGetResponseInterface>(uri.toString()));
};

/**
 * Get memberships by id.
 */
export const invite = ({
  /** Which entity to invite users to join. */
  fromId,
  /** Which role the entity will be a part of. */
  roleId,
  /** Type of entity to invite members to join. */
  type,
  /** Users identified by email that we wish to invite. */
  users,

  // DI API, mainly for testing.
  api = _api,
}: {
  fromId: ObjectId;
  roleId: string;
  type: keyof typeof StaffMembershipType;
  users: Array<string>;
  api?: Api;
}): Q.Promise<void> => {
  const typeToAction = {
    course: 'bulkInviteCourseStaffByEmails',
    partner: 'bulkInviteInstitutionStaffByEmails',
    program: 'bulkInviteDegreeStaffByEmails',
  };
  const action = typeToAction[type];
  const typeToIdKey = {
    course: 'courseId',
    partner: 'partnerId',
    program: 'degreeId',
  };
  const idKey = typeToIdKey[type];

  const uri = new URI().addQueryParam('action', action).addQueryParam(idKey, fromId).addQueryParam('roleId', roleId);

  return Q(
    api.post(uri.toString(), {
      data: users,
    })
  );
};

/**
 * Used during an update to existing members.
 */
export type StaffMembershipsAPIUpdateInterface = {
  /** Foreign key to role. */
  roleId: string;
  scope: {
    typeName: StaffMembershipTypeName;
    id: string | number;
  };
  /** Foreign key to user. */
  userId: number;
};

/**
 * Response to an update request will be an associative array of added and removed
 * memberships.
 */
export type StaffMembershipsAPIUpdateResponseInterface = {
  /** Newly created memberships. */
  addedMemberships: Array<StaffMembershipInterface>;
  /** Memberships removed as a side effect of the update operation. */
  deletedMemberships: Array<StaffMembershipInterface>;
};

/**
 * Update memberships for users.
 *
 * This is a replace operation, where any previously assigned memberships not
 * sent in this request and within the scope of the action (e.g. partner memberships for
 * an updateInstitutionStaffMemberships action) will be deleted.
 */
export const update = ({
  /** Which entity to invite users to join. */
  fromId,
  /** One membership object for each new membership for each user. */
  memberships,
  /** Type of entity to invite members to join. */
  type,

  // DI API, mainly for testing.
  api = _api,
}: {
  fromId: string | number;
  memberships: Array<StaffMembershipsAPIUpdateInterface>;
  type: keyof typeof StaffMembershipType;

  api?: Api;
}): Q.Promise<StaffMembershipsAPIUpdateResponseInterface> => {
  const typeToAction = {
    course: 'updateCourseStaffMemberships',
    partner: 'updateInstitutionStaffMemberships',
    program: 'updateDegreeStaffMemberships',
  };
  const action = typeToAction[type];
  const typeToIdKey = {
    course: 'courseId',
    partner: 'partnerId',
    program: 'degreeId',
  };
  const idKey = typeToIdKey[type];

  const uri = new URI().addQueryParam('action', action).addQueryParam(idKey, fromId);

  return Q(
    api.post<StaffMembershipsAPIUpdateResponseInterface>(uri.toString(), {
      data: memberships,
    })
  );
};
